/**
 * @file    RollupMetricModel.ts
 * @scope   SHARED
 * @summary Domain model for the rollup metrics authored in the CPR drawer (one row per metric).
 * @consumes RollupProgramModel, RollupCalculationModel
 * @produces RollupMetricModel, DEFAULT_ROLLUP_METRIC_NAME, createRollupMetric,
 *           createRollupProgram, getRollupProgramCount, isDefaultRollupMetricName
 * @sideEffects None (pure).
 * @notes Mirrors `models/Enterprise/*`. The backend contract (docs/guardrails.md) allows ONE
 *        rollup per widget (`CPR_MAX_ROLLUP_METRICS`). Persisted mapping:
 *        - `calculations` → widget-level `CustomCalculations` (apply to every program);
 *        - `programs`     → `QueryCollections` (symmetric `DataCollectionGuid` + `OriginId` peers).
 *        `id` and `displayName` are drawer-only (no backend field yet).
 *        TODO(pending): `calculationType` / `precision` / `partitionFilters` are DRAWER-ONLY.
 *        They must be folded into `calculations` (CustomCalculations) and the widget-level
 *        `PartitionFilter` before being persisted.
 */
import { FieldFilter } from 'models/Enterprise/FieldsManagerModel';
import { RollupProgramModel, createRollupProgramId } from './RollupProgramModel';
import { RollupCalculationModel } from './RollupCalculationModel';

export type { RollupProgramModel } from './RollupProgramModel';

/** One rollup metric: the same calculations rolled up across several programs. */
export interface RollupMetricModel {
  /** Stable client id (keys, drag-and-drop, navigation to "Manage programs"). */
  id: string;
  /** User-editable title. Drawer-only. */
  displayName: string;
  /** GuardRails `CustomCalculations` for CrossProgramRollup; applied to ALL programs. */
  calculations: RollupCalculationModel[];
  /** Contributor programs, in order. */
  programs: RollupProgramModel[];
  /** PENDING (drawer-only): calculation applied to the rolled-up question. */
  calculationType?: string;
  /** PENDING (drawer-only): number of decimal places of the result. */
  precision?: number;
  /** PENDING (drawer-only): maps to the widget-level `PartitionFilter`. */
  partitionFilters?: FieldFilter[];
}

export const DEFAULT_ROLLUP_METRIC_NAME = 'Cross program rollup';

/**
 * Builds a new, empty rollup metric.
 * @param position 1-based position used in the default name.
 */
export const createRollupMetric = (position: number): RollupMetricModel => ({
  id: `rollup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  displayName: `${DEFAULT_ROLLUP_METRIC_NAME} ${position}`,
  calculations: [],
  programs: [],
});

/** Builds a new, blank program row (nothing selected yet). */
export const createRollupProgram = (): RollupProgramModel => ({
  id: createRollupProgramId(),
  dataCollectionGuid: '',
  originId: '',
});

/** @returns Number of contributor programs of the metric. */
export const getRollupProgramCount = (metric: RollupMetricModel): number => metric.programs.length;

const DEFAULT_NAME_PATTERN = new RegExp(`^${DEFAULT_ROLLUP_METRIC_NAME} \\d+$`);

/** @returns true when the name is still the auto-generated one ("Cross program rollup N"). */
export const isDefaultRollupMetricName = (displayName: string): boolean =>
  DEFAULT_NAME_PATTERN.test(displayName.trim());
